import { APP_CONFIG, ACTIVITY_TYPES, PACKAGE_INFO } from '../config/contract';

const TELEGRAM_API = APP_CONFIG.telegramApiUrl;

// Activity message formatters for different types
const ACTIVITY_MESSAGES = {
  0: (data, userId) => ({ // Registration
    title: '🚀 New Registration',
    emoji: '🚀',
    details: [
      `User ID: ${userId}`,
      `Entry Unit: Genesis ($10)`,
      `Max Return: 3x ($30)`,
      `Status: ✅ Active`
    ]
  }),
  
  1: (data, userId) => ({ // Package Upgrade
    title: '⬆️ Unit Activated',
    emoji: '⬆️',
    details: [
      `Unit: ${PACKAGE_INFO[data.packageId]?.name || `Unit ${data.packageId}`}`,
      `Investment: $${parseFloat(data.amount).toFixed(2)} USDT`,
      `Max Return: 3x ($${(parseFloat(data.amount) * 3).toFixed(2)})`,
      `Status: ✅ Active`
    ]
  }),
  
  2: (data, userId) => ({ // Direct Referral Income
    title: '💎 Direct Referral Income',
    emoji: '💎',
    details: [
      `Income: +$${parseFloat(data.amount).toFixed(2)} USDT`,
      `From: User #${data.fromId}`,
      `Type: 36% Direct Bonus`,
      `Status: Instant to Wallet`
    ]
  }),
  
  3: (data, userId) => ({ // Level Income
    title: '📊 Level Income',
    emoji: '📊',
    details: [
      `Income: +$${parseFloat(data.amount).toFixed(2)} USDT`,
      `From: User #${data.fromId}`,
      `Type: 2.7% Level Bonus`,
      `Status: Added to Claimable`
    ]
  }),
  
  4: (data, userId) => ({ // Pool Income
    title: '🌊 Pool Income',
    emoji: '🌊',
    details: [
      `Income: +$${parseFloat(data.amount).toFixed(2)} USDT`,
      `Pool: ${PACKAGE_INFO[data.packageId]?.name || `Pool ${data.packageId}`}`,
      `Type: Pool Distribution`,
      `Status: Added to Claimable`
    ]
  }),
  
  5: (data, userId) => ({ // Claimed
    title: '✅ Withdrawal Successful',
    emoji: '✅',
    details: [
      `Amount: $${parseFloat(data.amount).toFixed(2)} USDT`,
      `Status: ✅ Sent to Wallet`,
      `Transaction: Completed`
    ]
  }),
  
  6: (data, userId) => ({ // Retopup
    title: '🔄 Retopup Successful',
    emoji: '🔄',
    details: [
      `Unit: ${PACKAGE_INFO[data.packageId]?.name || `Unit ${data.packageId}`}`,
      `Amount: $${parseFloat(data.amount).toFixed(2)} USDT`,
      `Ceiling Extended!`,
      `Status: ✅ Active`
    ]
  }),
  
  7: (data, userId) => ({ // Royalty Income
    title: '👑 Royalty Income',
    emoji: '👑',
    details: [
      `Income: +$${parseFloat(data.amount).toFixed(2)} USDT`,
      `Pool: Royalty Pool (5%)`,
      `Type: Royalty Distribution`,
      `Status: Added to Claimable`
    ]
  })
};

/**
 * Format activity message for Telegram
 */
const formatActivityMessage = (activity, userId) => {
  const type = activity.activityType;
  const formatter = ACTIVITY_MESSAGES[type];
  
  if (!formatter) {
    const info = ACTIVITY_TYPES[type] || { name: 'Activity', icon: '📌' };
    return formatGenericMessage(activity, userId, info);
  }
  
  const data = {
    amount: activity.amount,
    packageId: activity.packageId,
    fromId: activity.fromId
  };
  
  const msg = formatter(data, userId);
  const timestamp = new Date(activity.timestamp * 1000).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  
  let message = `${msg.emoji} *${msg.title}*\n\n`;
  message += `👤 User ID: \`${userId}\`\n\n`;
  
  msg.details.forEach(detail => {
    message += `• ${detail}\n`;
  });
  
  message += `\n📅 ${timestamp}\n`;
  message += `\n---\n_Time to Time - Journey Through DeFi_`;
  
  return message;
};

/**
 * Format generic activity message
 */
const formatGenericMessage = (activity, userId, info) => {
  const timestamp = new Date(activity.timestamp * 1000).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  let message = `${info.icon} *${info.name}*\n\n`;
  message += `👤 User ID: \`${userId}\`\n`;
  
  if (parseFloat(activity.amount) > 0) {
    message += `💰 Amount: $${parseFloat(activity.amount).toFixed(2)} USDT\n`;
  }
  
  if (activity.packageId > 0) {
    const pkg = PACKAGE_INFO[activity.packageId];
    message += `📦 Unit: ${pkg?.name || `Unit ${activity.packageId}`}\n`;
  }
  
  if (activity.fromId > 0) {
    message += `👥 From: User #${activity.fromId}\n`;
  }
  
  message += `\n📅 ${timestamp}\n`;
  message += `\n---\n_Time to Time - Journey Through DeFi_`;
  
  return message;
};

/**
 * Send Telegram notification for activity
 */
export const sendTelegramNotification = async (activity, userId) => {
  try {
    const chatId = localStorage.getItem('timetotime_telegram_id');
    if (!chatId) {
      console.log('Telegram not linked, skipping notification');
      return false;
    }

    const message = formatActivityMessage(activity, userId);
    
    const response = await fetch(TELEGRAM_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chat_id: chatId,
        message: message,
        parse_mode: 'Markdown'
      })
    });

    const result = await response.json();
    
    if (result.success) {
      console.log('Telegram notification sent:', ACTIVITY_TYPES[activity.activityType]?.name);
      return true;
    } else {
      console.error('Telegram notification failed:', result.message);
      return false;
    }
  } catch (error) {
    console.error('Error sending Telegram notification:', error);
    return false;
  }
};

/**
 * Send portfolio report to Telegram
 */
export const sendTelegramReport = async (chatId, reportData) => {
  try {
    const response = await fetch(TELEGRAM_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chat_id: chatId,
        report: reportData
      })
    });
    return await response.json();
  } catch (error) {
    console.error('Error sending report:', error);
    return { success: false, message: error.message };
  }
};

/**
 * Send notification for specific activity type
 */
export const notifyActivity = async (type, data, userId) => {
  const activity = {
    activityType: type,
    amount: data.amount || 0,
    packageId: data.packageId || 0,
    fromId: data.fromId || 0,
    timestamp: Math.floor(Date.now() / 1000)
  };
  
  return sendTelegramNotification(activity, userId);
};

/**
 * Check if Telegram is linked
 */
export const isTelegramLinked = () => {
  return !!localStorage.getItem('timetotime_telegram_id');
};

/**
 * Get Telegram chat ID
 */
export const getTelegramChatId = () => {
  return localStorage.getItem('timetotime_telegram_id');
};

/**
 * Set Telegram chat ID
 */
export const setTelegramChatId = (chatId) => {
  localStorage.setItem('timetotime_telegram_id', chatId);
};

/**
 * Remove Telegram chat ID
 */
export const removeTelegramChatId = () => {
  localStorage.removeItem('timetotime_telegram_id');
};

export default sendTelegramNotification;
